import {inject, Injectable} from '@angular/core';
import {HttpRequest, HttpHandler, HttpEvent, HttpInterceptor} from '@angular/common/http';
import {Observable, Subject, timer, takeUntil, switchMap, tap, finalize} from 'rxjs';
import {Store} from '@ngrx/store';
import {GetActiveUserAction} from '../../store/active-user/active-user.action';
import {Router} from '@angular/router';
import {MENU_PATHS} from 'src/app/app.constants';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable()
export class UserProfileInterceptor implements HttpInterceptor {
  private requestInProgress = new Subject<void>();
  private idleTimer = timer(1500); // 1.5 seconds idle timer
  private destroy$ = new Subject<void>();
  private store$ = inject(Store);
  private router = inject(Router);

  constructor() {
    // Set up the idle detection
    this.setupIdleDetection();
  }

  private setupIdleDetection(): void {
    const subscription = this.requestInProgress
      .pipe(
        tap(() => {
          // Reset the idle timer on each request
          this.destroy$.next();
        }),
        switchMap(() =>
          this.idleTimer.pipe(
            takeUntil(this.requestInProgress),
            tap(() => {
              this.store$.dispatch(new GetActiveUserAction({}));
            })
          )
        ),
        finalize(() => {
          this.cleanup();
          subscription.unsubscribe();
        })
      )
      .subscribe();
  }

  private shouldSkipNotification(request: HttpRequest<any>): boolean {
    const currentRoute = this.router.url;
    const skipRules = [
      {url: '/users/profile', methods: ['GET'], appRoute: '*'},
      {url: '/user/count', methods: ['GET'], appRoute: '*'},
      {url: '/rewards/', methods: ['GET'], appRoute: '*'},
      {url: '/teams', methods: ['GET'], appRoute: MENU_PATHS.CurrentSked},
    ];

    return skipRules.some(rule => {
      const urlMatch = request.url.includes(rule.url);
      const methodMatch = rule.methods.includes(request.method);
      let routeMatch = true;
      if (rule.appRoute) {
        if (rule.appRoute === '*') {
          routeMatch = true;
        } else {
          routeMatch = currentRoute.startsWith(rule.appRoute);
        }
      }
      return urlMatch && methodMatch && routeMatch;
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldSkipNotification(request)) {
      return next.handle(request);
    }
    // Pass the request through
    return next.handle(request).pipe(
      tap({
        complete: () => {
          this.requestInProgress.next();
        },
      })
    );
  }

  // Clean up resources when the interceptor is no longer needed
  private cleanup(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.requestInProgress.complete();
  }
}
